import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, LayoutDashboard, LogIn, Menu, Search, ShoppingCart, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { getProductPath, getProducts } from '../services/productCatalog';

export default function Header() {
  const { lang, setLang, t } = useLanguage();
  const { cartItems } = useCart();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [masterProducts, setMasterProducts] = useState([]);
  const [catalogError, setCatalogError] = useState('');

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 24);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    let active = true;
    getProducts()
      .then((products) => {
        if (active) setMasterProducts(products);
      })
      .catch(() => {
        if (active) setCatalogError('Produk belum dapat dimuat');
      });
    return () => { active = false; };
  }, []);

  const closeMenus = () => {
    setOpenMenu(null);
    setLanguageOpen(false);
  };

  const selectLanguage = (nextLanguage) => {
    setLang(nextLanguage);
    setLanguageOpen(false);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    if (!searchQuery.trim()) return;
    navigate(`/pencarian?q=${encodeURIComponent(searchQuery.trim())}`);
    setMobileMenuOpen(false);
  };

  const navClass = ({ isActive }) => `nav-link${isActive ? ' active' : ''}`;
  const productActive = location.pathname.startsWith('/produk-layanan');
  const aboutActive = ['/tentang-kami', '/lokasi-operasional-kami', '/sertifikat-penghargaan'].some(
    (path) => location.pathname.startsWith(path),
  );
  const productsByCategory = masterProducts.reduce((groups, product) => {
    const category = product.category || 'Produk Lainnya';
    if (!groups[category]) groups[category] = [];
    groups[category].push(product);
    return groups;
  }, {});

  return (
    <header className={`sbb-header${isScrolled ? ' sticky-scrolled' : ''}`}>
      <div className="header-top-line" />
      <div className="container header-content">
        <Link to="/" className="brand-logo" aria-label="Solusi Bangun Beton - Beranda">
          <img src="/logo_sbb.png" alt="Solusi Bangun Beton" className="brand-logo-image" />
        </Link>

        <nav className="desktop-nav" aria-label="Navigasi utama">
          <NavLink to="/" end className={navClass}>Beranda</NavLink>

          <div
            className={`nav-group${productActive ? ' active' : ''}`}
            onMouseEnter={() => setOpenMenu('products')}
            onMouseLeave={() => setOpenMenu(null)}
          >
            <Link to="/produk-layanan" className="nav-link nav-link-with-arrow">
              Produk &amp; Layanan <ChevronDown size={15} strokeWidth={2.5} />
            </Link>
            {openMenu === 'products' && (
              <div className="dropdown-panel products-dropdown">
                <Link to="/produk-layanan" className="dropdown-overview" onClick={closeMenus}>
                  <span>Semua Produk &amp; Layanan</span>
                  <span aria-hidden="true">→</span>
                </Link>
                {catalogError ? (
                  <p className="dropdown-status">{catalogError}</p>
                ) : masterProducts.length === 0 ? (
                  <p className="dropdown-status">Memuat produk...</p>
                ) : (
                  <div className="product-menu-grid">
                    {Object.entries(productsByCategory).map(([category, products]) => (
                      <div className="product-menu-group" key={category}>
                        <div className="product-menu-title">{category}</div>
                        {products.map((product) => (
                          <Link
                            key={product.id}
                            to={getProductPath(product)}
                            className="dropdown-product-link"
                            onClick={closeMenus}
                          >
                            <span>{product.name}</span>
                            {product.strength_class && product.strength_class !== 'N/A' && <small>{product.strength_class}</small>}
                          </Link>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div
            className={`nav-group${aboutActive ? ' active' : ''}`}
            onMouseEnter={() => setOpenMenu('about')}
            onMouseLeave={() => setOpenMenu(null)}
          >
            <Link to="/tentang-kami" className="nav-link nav-link-with-arrow">
              Tentang Kami <ChevronDown size={15} strokeWidth={2.5} />
            </Link>
            {openMenu === 'about' && (
              <div className="dropdown-panel about-dropdown">
                <Link to="/tentang-kami" className="dropdown-link" onClick={closeMenus}>Profil Perusahaan</Link>
                <Link to="/lokasi-operasional-kami" className="dropdown-link" onClick={closeMenus}>Lokasi Operasional</Link>
                <Link to="/sertifikat-penghargaan" className="dropdown-link" onClick={closeMenus}>Sertifikat &amp; Penghargaan</Link>
              </div>
            )}
          </div>

          <NavLink to="/pelanggan-proyek" className={navClass}>Pelanggan &amp; Proyek</NavLink>
          <NavLink to="/keberlanjutan" className={navClass}>Keberlanjutan</NavLink>
          <NavLink to="/blog" className={navClass}>Berita &amp; Artikel</NavLink>
        </nav>

        <div className="header-actions">
          <div className="language-menu">
            <button
              type="button"
              className="language-trigger"
              onClick={() => setLanguageOpen((value) => !value)}
              aria-expanded={languageOpen}
            >
              <span className="language-flag" />
              <span>{lang === 'id' ? 'Indonesian' : 'English'}</span>
              <ChevronDown size={18} />
            </button>
            {languageOpen && (
              <div className="language-options">
                <button type="button" onClick={() => selectLanguage('id')} className={lang === 'id' ? 'active' : ''}>Indonesian</button>
                <button type="button" onClick={() => selectLanguage('en')} className={lang === 'en' ? 'active' : ''}>English</button>
              </div>
            )}
          </div>

          <Link to="/cart" className="header-cart-button" aria-label={`Keranjang, ${cartItems.length} produk`}>
            <ShoppingCart size={21} />
            {cartItems.length > 0 && <span>{cartItems.length}</span>}
          </Link>

          {user ? (
            <Link to="/portal" className="contact-button dashboard-button">
              <LayoutDashboard size={16} />
              {lang === 'id' ? 'DASHBOARD' : 'DASHBOARD'}
            </Link>
          ) : (
            <Link to="/login" className="contact-button login-button">
              <LogIn size={16} />
              {lang === 'id' ? 'LOGIN' : 'LOGIN'}
            </Link>
          )}

          <button
            type="button"
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen((value) => !value)}
            aria-label="Buka menu navigasi"
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X size={26} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="mobile-drawer">
          <nav className="mobile-nav" aria-label="Navigasi mobile" onClick={(event) => {
            if (event.target.closest('a')) setMobileMenuOpen(false);
          }}>
            <NavLink to="/" end>Beranda</NavLink>
            <details>
              <summary>Produk &amp; Layanan <ChevronDown size={17} /></summary>
              <div className="mobile-submenu">
                <Link to="/produk-layanan">Semua Produk &amp; Layanan</Link>
                {masterProducts.map((product) => (
                  <Link key={product.id} to={getProductPath(product)}>
                    {product.name}
                  </Link>
                ))}
              </div>
            </details>
            <details>
              <summary>Tentang Kami <ChevronDown size={17} /></summary>
              <div className="mobile-submenu">
                <Link to="/tentang-kami">Profil Perusahaan</Link>
                <Link to="/lokasi-operasional-kami">Lokasi Operasional</Link>
                <Link to="/sertifikat-penghargaan">Sertifikat &amp; Penghargaan</Link>
              </div>
            </details>
            <NavLink to="/pelanggan-proyek">Pelanggan &amp; Proyek</NavLink>
            <NavLink to="/keberlanjutan">Keberlanjutan</NavLink>
            <NavLink to="/blog">Berita &amp; Artikel</NavLink>
            <Link to="/cart">Keranjang ({cartItems.length})</Link>
            <Link to="/kontak-kami" className="mobile-contact">Kontak</Link>
          </nav>

          <form onSubmit={handleSearchSubmit} className="mobile-search-form">
            <Search size={18} />
            <input
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder={t('search_placeholder')}
              aria-label={t('search_placeholder')}
            />
            <button type="submit">Cari</button>
          </form>
        </div>
      )}

      <style>{`
        .sbb-header {
          position: sticky;
          top: 0;
          z-index: 1000;
          background: #ffffff;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.09);
        }

        .sbb-header.sticky-scrolled { box-shadow: 0 5px 18px rgba(0, 0, 0, 0.12); }
        .header-top-line { height: 2px; background: #2c2c2c; }

        .header-content {
          min-height: 90px;
          display: grid;
          grid-template-columns: 178px minmax(0, 1fr) auto;
          align-items: center;
          gap: 32px;
        }

        .brand-logo { display: flex; align-items: center; }
        .brand-logo-image { width: 168px; height: 58px; object-fit: contain; }

        .desktop-nav {
          height: 100%;
          display: flex;
          align-items: stretch;
          justify-content: center;
          gap: clamp(18px, 1.55vw, 32px);
          white-space: nowrap;
        }

        .nav-link,
        .nav-group {
          position: relative;
          display: flex;
          align-items: center;
          color: #292929;
          font-size: 15px;
          font-weight: 600;
        }

        .nav-link { height: 100%; }
        .nav-link-with-arrow { gap: 7px; }

        .nav-link::after,
        .nav-group::after {
          content: '';
          position: absolute;
          left: 0;
          right: 100%;
          bottom: 0;
          height: 3px;
          background: #e5242a;
          transition: right 0.22s ease;
        }

        .nav-link:hover,
        .nav-link.active,
        .nav-group:hover > .nav-link,
        .nav-group.active > .nav-link { color: #e5242a; }
        .nav-link:hover::after,
        .nav-link.active::after,
        .nav-group:hover::after,
        .nav-group.active::after { right: 0; }
        .nav-group .nav-link::after { display: none; }

        .dropdown-panel {
          position: absolute;
          top: calc(100% - 1px);
          left: -22px;
          background: #ffffff;
          border: 1px solid #e6e8eb;
          box-shadow: 0 14px 36px rgba(0, 0, 0, 0.14);
          padding: 14px;
          color: #292929;
        }

        .products-dropdown { width: min(760px, 72vw); }
        .about-dropdown { width: 270px; }
        .product-menu-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 20px 22px;
          max-height: 480px;
          overflow-y: auto;
          padding: 8px 10px 10px;
        }
        .product-menu-title {
          padding: 0 8px 8px;
          border-bottom: 2px solid #bbd448;
          color: #777654;
          font-size: 12px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.4px;
        }
        .dropdown-product-link {
          display: flex;
          justify-content: space-between;
          gap: 8px;
          padding: 9px 8px;
          border-bottom: 1px solid #eeeeea;
          color: #333;
          font-size: 12px;
          font-weight: 600;
        }
        .dropdown-product-link:hover { background: #f6f6f2; color: #e5242a; }
        .dropdown-product-link small { color: #898989; font-size: 10px; }
        .dropdown-status { padding: 24px 12px; color: #777; font-size: 13px; text-align: center; }

        .dropdown-overview,
        .dropdown-link {
          display: flex;
          align-items: center;
          justify-content: space-between;
          min-height: 42px;
          padding: 9px 12px;
          font-size: 13px;
          font-weight: 600;
          transition: background 0.18s ease, color 0.18s ease;
        }

        .dropdown-overview {
          margin-bottom: 8px;
          border-bottom: 1px solid #ececec;
          color: #777654;
          font-weight: 800;
        }

        .dropdown-link:hover,
        .dropdown-overview:hover { background: #f5f5f1; color: #e5242a; }

        .header-actions { display: flex; align-items: center; gap: 20px; }
        .language-menu { position: relative; }

        .language-trigger {
          width: 174px;
          height: 54px;
          display: grid;
          grid-template-columns: 18px 1fr 18px;
          align-items: center;
          gap: 10px;
          padding: 0 16px;
          border: 1px solid #dfe3e7;
          border-radius: 7px;
          color: #26394c;
          font-size: 15px;
          font-weight: 500;
          text-align: left;
          background: #ffffff;
        }

        .language-flag { width: 18px; height: 7px; border-radius: 2px 2px 0 0; background: #ed1c24; }
        .language-options {
          position: absolute;
          top: calc(100% + 8px);
          left: 0;
          right: 0;
          padding: 6px;
          background: #ffffff;
          border: 1px solid #dfe3e7;
          box-shadow: 0 10px 24px rgba(0,0,0,0.12);
          border-radius: 6px;
        }
        .language-options button { width: 100%; padding: 10px; text-align: left; font-size: 13px; }
        .language-options button:hover,
        .language-options button.active { background: #f2f3f4; color: #e5242a; }

        .contact-button {
          min-width: 104px;
          height: 54px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 0 20px;
          border-radius: 7px;
          background: #e5242a;
          color: #ffffff;
          font-size: 14px;
          font-weight: 700;
          letter-spacing: 0.4px;
          transition: background 0.2s ease, transform 0.2s ease;
        }
        .login-button {
          background: #777654;
        }
        .login-button:hover { background: #65644a !important; }
        .dashboard-button {
          background: #777654;
        }
        .dashboard-button:hover { background: #65644a !important; }
        .header-cart-button {
          position: relative;
          width: 46px;
          height: 54px;
          display: grid;
          place-items: center;
          color: #2c2c2c;
          border: 1px solid #dfe3e7;
          border-radius: 7px;
        }
        .header-cart-button:hover { color: #e5242a; border-color: #e5242a; }
        .header-cart-button span {
          position: absolute;
          top: -7px;
          right: -7px;
          min-width: 20px;
          height: 20px;
          padding: 0 5px;
          display: grid;
          place-items: center;
          border-radius: 10px;
          background: #e5242a;
          color: #fff;
          font-size: 10px;
          font-weight: 800;
        }
        .contact-button:hover { background: #c91e24; transform: translateY(-1px); }
        .mobile-menu-toggle { display: none; color: #292929; }
        .mobile-drawer { display: none; }

        @media (max-width: 1280px) {
          .header-content { grid-template-columns: 150px minmax(0, 1fr) auto; gap: 18px; }
          .brand-logo-image { width: 146px; }
          .desktop-nav { gap: 16px; }
          .language-trigger { width: 150px; }
          .header-actions { gap: 12px; }
        }

        @media (max-width: 1120px) {
          .header-content { min-height: 82px; grid-template-columns: 1fr auto; }
          .brand-logo-image { width: 148px; height: 50px; }
          .desktop-nav,
          .header-actions .language-menu,
          .header-actions .contact-button,
          .header-actions .header-cart-button { display: none; }
          .mobile-menu-toggle { display: flex; align-items: center; justify-content: center; }

          .mobile-drawer {
            display: block;
            max-height: calc(100vh - 84px);
            overflow-y: auto;
            padding: 12px 24px 24px;
            background: #ffffff;
            border-top: 1px solid #ececec;
          }
          .mobile-nav { display: flex; flex-direction: column; }
          .mobile-nav > a,
          .mobile-nav summary {
            min-height: 52px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            border-bottom: 1px solid #ececec;
            font-size: 15px;
            font-weight: 700;
            cursor: pointer;
            list-style: none;
          }
          .mobile-nav summary::-webkit-details-marker { display: none; }
          .mobile-submenu { padding: 8px 0 8px 16px; background: #f7f7f4; }
          .mobile-submenu a { display: block; padding: 10px 12px; font-size: 13px; color: #555; }
          .mobile-contact { margin-top: 14px; padding: 0 18px; justify-content: center !important; background: #e5242a; color: #fff; border: 0 !important; }
          .mobile-search-form {
            height: 48px;
            margin-top: 18px;
            display: grid;
            grid-template-columns: 20px 1fr auto;
            align-items: center;
            gap: 10px;
            padding-left: 14px;
            border: 1px solid #dfe3e7;
          }
          .mobile-search-form input { min-width: 0; border: 0; outline: 0; font: inherit; }
          .mobile-search-form button { height: 100%; padding: 0 18px; background: #777654; color: #fff; font-weight: 700; }
        }

        @media (max-width: 520px) {
          .header-content { min-height: 72px; padding-left: 18px; padding-right: 18px; }
          .brand-logo-image { width: 132px; height: 44px; }
          .mobile-drawer { padding-left: 18px; padding-right: 18px; }
        }
      `}</style>
    </header>
  );
}
